import { useMemo } from 'react';
import _get from 'lodash/get';
import { User, useWhoamiQuery } from 'graphql-common';
import { WatchQueryFetchPolicy } from '@apollo/client';

type Args = {
  fetchPolicy?: WatchQueryFetchPolicy;
};

export default function useWhoamiQueryHook(args?: Args) {
  const { fetchPolicy } = args || {};
  const { data, error, refetch, loading, networkStatus } = useWhoamiQuery({
    fetchPolicy: fetchPolicy || 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Memoized so this object stays reference-stable across renders that don't
  // touch whoami — callers sharing this via React Context (see
  // useWhoamiContext) would otherwise see a "changed" value, and re-render,
  // on every unrelated render of the component calling this hook.
  // Every `currentUser*` scalar is derived from `data` and so can't change
  // independently of it; deriving them inside the factory keeps the dep list
  // down to the query result's own five moving parts.
  return useMemo(() => {
    const currentUser = _get(data, 'whoami.resource') as User;
    return {
      currentUser,
      currentUserDeactivatedAt: currentUser?.deactivatedAt,
      currentUserEmail: currentUser?.email,
      currentUserId: currentUser?.id,
      currentUserLang: currentUser?.lang,
      currentUserUnconfirmedEmail: currentUser?.unconfirmedEmail,
      currentUserUsername: currentUser?.username,
      data,
      error,
      firstLoading: loading && data === undefined,
      loading,
      networkStatus,
      refetch,
    };
  }, [data, error, loading, networkStatus, refetch]);
}
